import React, { useContext, useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useParams } from 'react-router-dom';
import { DevicesContext } from '../../contexts/DevicesContext';
import { DeviceDashboardDto, ParameterDtoPreferred } from '../../services/deviceApi';
import { getTimezoneAbbreviation, getUserTimezone } from '../../utils/timezoneUtils';
import HelperFunctions from '../../utils/helperFunctions';

type PredictionRange = '1H' | '6H' | '1D';
type AqiRangeBucket =
  | 'excellent'
  | 'acceptable'
  | 'sensitive'
  | 'moderate'
  | 'poor'
  | 'hazardous';
type PredictionParameterKey = string;

interface AqiPoint {
  timestamp: string;
  aqi: number;
  pollutantConcentrations?: Record<string, number>;
  primaryPollutant?: string;
}

const PREDICTION_API_URL =
  process.env.REACT_APP_PREDICTION_API_URL || 'http://127.0.0.1:8000/predict';
const RANGE_DURATION_MS: Record<PredictionRange, number> = {
  '1H': 1 * 60 * 60 * 1000,
  '6H': 6 * 60 * 60 * 1000,
  '1D': 24 * 60 * 60 * 1000,
};
const predictionRequestCache = new Map<string, Promise<AqiPoint[]>>();
const predictionResponseCache = new Map<string, AqiPoint[]>();

const DEFAULT_PARAMETER_COLOR = '#005EE5';
const PARAMETER_COLOR_PALETTE = [
  '#9B59B6',
  '#2ECC71',
  '#F1C40F',
  '#16A085',
  '#E74C3C',
  '#3498DB',
  '#1ABC9C',
  '#E67E22',
  '#8E44AD',
  '#27AE60',
];

const POLLUTANT_CAUTION_PHRASES: Record<string, string[]> = {
  PM25: [
    'Fine particles (PM2.5) may be the dominant driver; keep doors and windows closed if outdoor air is not cleaner.',
    'PM2.5 is expected to dominate; run air purifiers with HEPA filters where possible.',
    'PM2.5 influence appears high; avoid activities that generate indoor smoke or dust.',
    'PM2.5 is likely the main pollutant; keep high-efficiency filtration active.',
    'PM2.5 may drive AQI; consider limiting movement in affected rooms during peaks.',
  ],
  PM10: [
    'PM10 is expected to be the key contributor; reduce dust-generating activities indoors.',
    'Coarse particles (PM10) may dominate; keep surfaces clean and limit resuspension of dust.',
    'PM10 is likely the main pollutant; consider controlled ventilation only when outside air is better.',
    'PM10 appears dominant; use purification and keep windows managed based on outdoor conditions.',
    'PM10 impact is expected to be high; avoid sweeping that can lift particles.',
  ],
  NO2: [
    'NO₂ is expected to contribute most; reduce combustion sources such as gas stoves where possible.',
    'NO₂ may dominate; improve kitchen exhaust and avoid unvented burning activities.',
    'NO₂ appears as the main pollutant; keep fresh-air circulation optimized when outdoor air permits.',
    'NO₂ influence is likely elevated; limit indoor combustion and monitor enclosed spaces closely.',
    'NO₂ is forecast as the primary driver; prioritize source control and targeted ventilation.',
  ],
  O3: [
    'O₃ appears likely to dominate; avoid bringing in outside air during high-ozone periods.',
    'O₃ may be the main pollutant; time ventilation for lower outdoor ozone intervals.',
    'O₃ influence is expected; keep windows managed and use recirculation plus filtration where possible.',
    'O₃ is likely a key contributor; minimize direct outdoor air intake during peak periods.',
    'O₃ may drive AQI changes; monitor trends before opening windows for long durations.',
  ],
  CO: [
    'CO may be a key contributor; ensure combustion appliances are ventilated properly.',
    'CO appears elevated; verify indoor combustion sources are controlled and vented safely.',
    'CO is likely influential; avoid enclosed combustion and maintain airflow from safe sources.',
    'CO may dominate AQI behavior; prioritize source checks and ventilation safety.',
    'CO impact is expected to rise; keep the area monitored and avoid fuel-burning activities indoors.',
  ],
  UNKNOWN: [
    'The dominant pollutant signal is mixed; continue active monitoring and adaptive ventilation.',
    'No single pollutant clearly dominates; maintain general air-quality precautions.',
    'Pollutant contribution appears distributed; keep purification and controlled airflow running.',
    'The main pollutant is uncertain in this window; follow broad indoor air best practices.',
    'A mixed pollutant pattern is expected; keep mitigation measures steady and consistent.',
  ],
};

const AQI_RANGE_LABELS: Record<AqiRangeBucket, string> = {
  excellent: 'Excellent',
  acceptable: 'Acceptable',
  sensitive: 'Sensitive',
  moderate: 'Unhealthy',
  poor: 'Very Unhealthy',
  hazardous: 'Hazardous',
};

/** Maps pollutant keys to a stable ASCII key. Unicode subscripts (e.g. C₆H₆) must become digits first; stripping alone turns C₆H₆ into "CH". */
const normalizeParameterName = (value: string) =>
  value
    .replace(/[\u2080-\u2089]/g, ch =>
      String.fromCharCode(ch.charCodeAt(0) - 0x2080 + 0x30)
    )
    .replace(/[^a-zA-Z0-9.]/g, '')
    .toUpperCase();

const getDominantPollutantKey = (pollutants: string[]): string => {
  if (!pollutants.length) return 'UNKNOWN';
  const counts = pollutants.reduce<Record<string, number>>((acc, p) => {
    const normalized = normalizeParameterName(p);
    acc[normalized] = (acc[normalized] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'UNKNOWN';
};

const makeCautionNonRepeating = (caution: string, pollutantDisplay: string) => {
  const cleaned = caution.trim();
  const escaped = pollutantDisplay.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const prefixRegex = new RegExp(`^${escaped}\\s+`, 'i');

  if (!prefixRegex.test(cleaned)) return cleaned;

  const tail = cleaned.replace(prefixRegex, '');
  if (/^(influence|impact)\b/i.test(tail)) return `Its ${tail}`;
  if (/^(is|may|appears|can|should|will)\b/i.test(tail)) return `It ${tail}`;
  if (/^expected\b/i.test(tail)) return `It is ${tail}`;
  return `This pollutant ${tail}`;
};

const toDisplayParameterLabel = (value: string) =>
  value
    .replace(/NO2/g, 'NO₂')
    .replace(/O3/g, 'O₃')
    .replace(/CO2/g, 'CO₂')
    .replace(/SO2/g, 'SO₂')
    .replace(/CH2O/g, 'CH₂O')
    .replace(/C6H6/gi, 'C₆H₆')
    .replace(/PM25/g, 'PM2.5');

const parsePredictionResponse = (data: any): AqiPoint[] =>
  (data?.predictions || []).map(
    (item: {
      timestamp: string;
      aqi: number;
      pollutant_concentrations?: Record<string, number>;
      primary_pollutant?: string;
    }) => ({
      timestamp: item.timestamp,
      aqi: item.aqi,
      pollutantConcentrations: Object.entries(item.pollutant_concentrations || {}).reduce<
        Record<string, number>
      >((acc, [pollutantName, pollutantValue]) => {
        const normalized = normalizeParameterName(pollutantName);
        if (normalized) {
          acc[normalized] = typeof pollutantValue === 'number' ? pollutantValue : 0;
        }
        return acc;
      }, {}),
      primaryPollutant: item.primary_pollutant || '',
    })
  );

const getPredictionsOnce = async (requestUrl: string): Promise<AqiPoint[]> => {
  const cachedResponse = predictionResponseCache.get(requestUrl);
  if (cachedResponse) {
    return cachedResponse;
  }

  const inflightRequest = predictionRequestCache.get(requestUrl);
  if (inflightRequest) {
    return inflightRequest;
  }

  const requestPromise = (async () => {
    const response = await fetch(requestUrl, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Prediction API failed with status ${response.status}`);
    }

    const data = await response.json();
    const parsedPredictions = parsePredictionResponse(data);
    predictionResponseCache.set(requestUrl, parsedPredictions);
    return parsedPredictions;
  })();

  predictionRequestCache.set(requestUrl, requestPromise);

  try {
    return await requestPromise;
  } finally {
    predictionRequestCache.delete(requestUrl);
  }
};

function DevicePredection({
  parametersForDevice,
  liveDeviceData,
}: {
  parametersForDevice: ParameterDtoPreferred[];
  liveDeviceData: DeviceDashboardDto;
}) {
  const { id } = useParams<{ id: string }>();
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }

  const theme = document.documentElement.getAttribute("data-theme") as 'light' | 'dark' ?? null;

  const { loading, deviceSensorData, getDeviceSensorData } = devicesContext;
  const [selectedRange, setSelectedRange] = useState<PredictionRange>('1H');
  const [predictionLoading, setPredictionLoading] = useState(false);
  const [predictionError, setPredictionError] = useState('');
  const [predictions, setPredictions] = useState<AqiPoint[]>([]);
  const [isSensorHistoryReady, setIsSensorHistoryReady] = useState(false);
  const [userSelectedParameter, setUserSelectedParameter] =
    useState<PredictionParameterKey>('aqi');
  const [userSelectedParameter2, setUserSelectedParameter2] = useState<
    PredictionParameterKey | ''
  >('');

  const userPreferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
  const timeZone = userPreferences.timezone || getUserTimezone();
  const aqiStandardId = userPreferences.aqiStandardId || 2;

  const standard =
    aqiStandardId === 1 ? 'US' : aqiStandardId === 3 ? 'IN' : 'UAE';

  const formatChemicalLabel = (value: string) => toDisplayParameterLabel(value);

  const colorByParameter = useMemo(() => {
    const uniqueNormalizedNames = Array.from(
      new Set(
        parametersForDevice
          .map(param => normalizeParameterName(param.parameterName || ''))
          .filter(Boolean)
      )
    );

    const colors: Record<string, string> = { AQI: DEFAULT_PARAMETER_COLOR };
    uniqueNormalizedNames.forEach((name, index) => {
      if (name !== 'AQI') {
        colors[name] = PARAMETER_COLOR_PALETTE[index % PARAMETER_COLOR_PALETTE.length];
      }
    });

    return colors;
  }, [parametersForDevice]);

  const actualAqiData = useMemo(() => {
    if (!deviceSensorData?.readings?.length || !id) return [];

    const readingsByTimestamp = new Map<string, typeof deviceSensorData.readings>();
    deviceSensorData.readings.forEach(reading => {
      const ts = new Date(reading.recordedAt).toISOString();
      const current = readingsByTimestamp.get(ts) || [];
      current.push(reading);
      readingsByTimestamp.set(ts, current);
    });

    return deviceSensorData.readings
      .filter(
        reading =>
          reading.parameterName === 'AQI' &&
          reading.parameterDriverId != null &&
          deviceSensorData.deviceId.toString() === id
      )
      .sort(
        (a, b) =>
          new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
      )
      .map(reading => ({
        timestamp: reading.recordedAt,
        aqi: reading.parameterValue,
        primaryPollutant: (() => {
          const ts = new Date(reading.recordedAt).toISOString();
          const matched = readingsByTimestamp.get(ts) || [];
          if (!matched.length) return '';
          const maxItem = matched.reduce((prev, current) =>
            current.subIndex > prev.subIndex ? current : prev
          );
          return maxItem?.parameterName || '';
        })(),
      }));
  }, [deviceSensorData, id]);

  const actualDataByParameter = useMemo(() => {
    const result: Record<
      PredictionParameterKey,
      Array<{ timestamp: string; value: number; primaryPollutant?: string }>
    > = {};

    if (!deviceSensorData?.readings?.length || !id) return result;

    const readingsByTimestamp = new Map<string, typeof deviceSensorData.readings>();
    deviceSensorData.readings.forEach(reading => {
      const ts = new Date(reading.recordedAt).toISOString();
      const current = readingsByTimestamp.get(ts) || [];
      current.push(reading);
      readingsByTimestamp.set(ts, current);
    });

    const filteredAndSortedReadings = deviceSensorData.readings
      .filter(
        reading =>
          reading.parameterDriverId != null && deviceSensorData.deviceId.toString() === id
      )
      .sort(
        (a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
      );

    filteredAndSortedReadings.forEach(reading => {
      const key = normalizeParameterName(reading.parameterName || '');
      if (!key) return;

      const ts = new Date(reading.recordedAt).toISOString();
      const matched = readingsByTimestamp.get(ts) || [];
      const maxItem = matched.length
        ? matched.reduce((prev, current) =>
            current.subIndex > prev.subIndex ? current : prev
          )
        : null;

      if (!result[key]) result[key] = [];
      result[key].push({
        timestamp: reading.recordedAt,
        value: reading.parameterValue,
        primaryPollutant: maxItem?.parameterName || '',
      });
    });

    return result;
  }, [deviceSensorData, id]);

  const predictionParameters = useMemo(() => {
    const keys = new Set<string>();
    keys.add('AQI');

    parametersForDevice.forEach(param => {
      const normalized = normalizeParameterName(param.parameterName || '');
      if (normalized) keys.add(normalized);
    });

    const orderedKeys = Array.from(keys).sort((a, b) => {
      if (a === 'AQI') return -1;
      if (b === 'AQI') return 1;
      return a.localeCompare(b);
    });

    return orderedKeys.map(key => ({
      key: key === 'AQI' ? 'aqi' : key,
      label: key === 'AQI' ? 'AQI' : toDisplayParameterLabel(key),
    }));
  }, [parametersForDevice]);

  const formatPredictDateTime = (date: Date) => date.toISOString();

  useEffect(() => {
    if (!id || !getDeviceSensorData) return;

    let isMounted = true;
    const toDate = liveDeviceData?.lastUpdated
      ? new Date(liveDeviceData.lastUpdated)
      : new Date();
    const fromDate = new Date(toDate.getTime() - 12 * 60 * 60 * 1000);

    setIsSensorHistoryReady(false);

    getDeviceSensorData({
      DeviceId: Number(id),
      FromDate: fromDate.toISOString(),
      ToDate: toDate.toISOString(),
    }).finally(() => {
      if (isMounted) {
        setIsSensorHistoryReady(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [id, getDeviceSensorData, liveDeviceData?.lastUpdated]);

  useEffect(() => {
    if (!id || !isSensorHistoryReady) return;

    const fetchPredictions = async () => {
      try {
        setPredictionLoading(true);
        setPredictionError('');

        const latestActualTimestamp =
          actualAqiData[actualAqiData.length - 1]?.timestamp ||
          liveDeviceData?.lastUpdated ||
          new Date().toISOString();

        const fromDate = new Date(latestActualTimestamp);
        if (Number.isNaN(fromDate.getTime())) {
          throw new Error('Invalid source timestamp for prediction');
        }
        const toDate = new Date(
          fromDate.getTime() + RANGE_DURATION_MS[selectedRange]
        );
        if (Number.isNaN(toDate.getTime())) {
          throw new Error('Invalid prediction end timestamp');
        }

        const query = new URLSearchParams({
          device_id: id,
          standard,
          from: formatPredictDateTime(fromDate),
          to: formatPredictDateTime(toDate),
        });
        const predictedData = await getPredictionsOnce(
          `${PREDICTION_API_URL}?${query.toString()}`
        );
        setPredictions(predictedData);
      } catch (error) {
        setPredictions([]);
        setPredictionError(
          error instanceof Error && error.message
            ? error.message
            : 'Unable to load prediction data.'
        );
      } finally {
        setPredictionLoading(false);
      }
    };

    fetchPredictions();
  }, [
    actualAqiData,
    id,
    isSensorHistoryReady,
    liveDeviceData?.lastUpdated,
    selectedRange,
    standard,
  ]);

  const getPredictedValueByKey = (
    point: AqiPoint,
    key: PredictionParameterKey
  ): number => {
    if (key === 'aqi') return point.aqi ?? 0;
    return point.pollutantConcentrations?.[normalizeParameterName(key)] ?? 0;
  };

  const getSeriesName = (key: PredictionParameterKey) =>
    predictionParameters.find(param => param.key === key)?.label || key;

  const getAqiRangeBucket = (aqiAvg: number): AqiRangeBucket => {
    if (aqiAvg <= 50) return 'excellent';
    if (aqiAvg <= 100) return 'acceptable';
    if (aqiAvg <= 150) return 'sensitive';
    if (aqiAvg <= 200) return 'moderate';
    if (aqiAvg <= 300) return 'poor';
    return 'hazardous';
  };

  const pickStableSentence = (seed: string, pool: string[]) => {
    if (!pool.length) return '';
    const hash = seed.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return pool[hash % pool.length];
  };

  const predictionAdvisory = useMemo(() => {
    if (!predictions.length) return '';

    const avgAqi =
      predictions.reduce((sum, point) => sum + (point.aqi || 0), 0) / predictions.length;
    const roundedAvgAqi = Number(avgAqi.toFixed(1));
    const aqiRange = getAqiRangeBucket(roundedAvgAqi);
    const rangeLabel = AQI_RANGE_LABELS[aqiRange];

    const dominantPollutantRaw = getDominantPollutantKey(
      predictions.map(p => p.primaryPollutant || '').filter(Boolean)
    );

    const pollutantKeyForAdvice =
      dominantPollutantRaw in POLLUTANT_CAUTION_PHRASES
        ? dominantPollutantRaw
        : dominantPollutantRaw === 'PM2.5'
          ? 'PM25'
          : dominantPollutantRaw;

    const pollutantDisplay =
      pollutantKeyForAdvice === 'UNKNOWN'
        ? 'mixed pollutants'
        : formatChemicalLabel(pollutantKeyForAdvice);

    const rangeText = selectedRange === '1H' ? '1 Hour' : selectedRange === '6H' ? '6 Hours' : '24 Hours';
    const intro = `Outlook for the next ${rangeText}:`;
    const caution = pickStableSentence(
      `${selectedRange}-${pollutantKeyForAdvice}-${aqiRange}-caution`,
      POLLUTANT_CAUTION_PHRASES[pollutantKeyForAdvice] ||
        POLLUTANT_CAUTION_PHRASES.UNKNOWN
    );
    const normalizedCaution = makeCautionNonRepeating(caution, pollutantDisplay);

    return {
      title: intro,
      body: `Avg AQI ${roundedAvgAqi} (${rangeLabel}); mainly ${pollutantDisplay}. ${normalizedCaution}`,
    };
  }, [predictions, selectedRange]);

  const handleParameterClick = (parameterKey: PredictionParameterKey) => {
    if (userSelectedParameter === parameterKey && userSelectedParameter2) {
      setUserSelectedParameter(userSelectedParameter2);
      setUserSelectedParameter2('');
      return;
    }

    if (userSelectedParameter2 === parameterKey) {
      setUserSelectedParameter2('');
      return;
    }

    if (parameterKey !== userSelectedParameter && !userSelectedParameter2) {
      setUserSelectedParameter2(parameterKey);
      return;
    }

    if (
      parameterKey !== userSelectedParameter &&
      parameterKey !== userSelectedParameter2
    ) {
      setUserSelectedParameter(userSelectedParameter2 || userSelectedParameter);
      setUserSelectedParameter2(parameterKey);
    }
  };

  const chartOptions = useMemo((): Highcharts.Options => {
    const selectedParameters: PredictionParameterKey[] = userSelectedParameter2
      ? [userSelectedParameter, userSelectedParameter2]
      : [userSelectedParameter];

    const buildSeriesForParameter = (
      key: PredictionParameterKey,
      yAxisIndex: number
    ): Highcharts.SeriesLineOptions[] => {
      const actualPoints =
        key === 'aqi'
          ? actualDataByParameter.AQI || []
          : actualDataByParameter[normalizeParameterName(key)] || [];
      const actualSeriesData = actualPoints.map(point => ({
        x: new Date(point.timestamp).getTime(),
        y: point.value,
        custom: {
          primaryPollutant: point.primaryPollutant || '',
          parameterKey: key,
          segment: 'actual',
        },
      }));

      const lastActualTimestamp = actualSeriesData[actualSeriesData.length - 1]?.x ?? 0;
      const lastActualValue = actualSeriesData[actualSeriesData.length - 1]?.y;
      const predictionSeriesData = predictions
        .map(point => ({
          x: new Date(point.timestamp).getTime(),
          y: getPredictedValueByKey(point, key),
          custom: {
            primaryPollutant: point.primaryPollutant || '',
            parameterKey: key,
            segment: 'prediction',
          },
        }))
        .filter(point => point.x > lastActualTimestamp);

      // Add a bridge point so dotted prediction connects with solid actual line.
      if (
        lastActualTimestamp &&
        typeof lastActualValue === 'number' &&
        predictionSeriesData.length > 0
      ) {
        predictionSeriesData.unshift({
          x: lastActualTimestamp,
          y: lastActualValue,
          custom: {
            primaryPollutant:
              actualSeriesData[actualSeriesData.length - 1]?.custom?.primaryPollutant || '',
            parameterKey: key,
            segment: 'prediction',
          },
        });
      }

      const hasConstantAqi =
        actualSeriesData.length > 0 &&
        actualSeriesData.every(p => p.y === actualSeriesData[0].y);

      return [
        {
          type: 'line',
          name: getSeriesName(key),
          data: actualSeriesData,
          color: key === 'aqi'
            ? (actualSeriesData.length <= 1 || hasConstantAqi
                ? HelperFunctions.GetParameterColor(actualSeriesData[0]?.y, 'AQI')
                : ({
                    linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
                    stops: actualSeriesData.map((point, index) => [
                      index / (actualSeriesData.length - 1),
                      HelperFunctions.GetParameterColor(point.y, 'AQI'),
                    ]),
                  } as any))
            : colorByParameter[normalizeParameterName(key)] || DEFAULT_PARAMETER_COLOR,
          lineWidth: 3,
          marker: { enabled: false },
          yAxis: yAxisIndex,
          showInLegend: false,
        },
        {
          type: 'line',
          name: `${getSeriesName(key)} (Predicted)`,
          data: predictionSeriesData,
          color: colorByParameter[normalizeParameterName(key)] || DEFAULT_PARAMETER_COLOR,
          lineWidth: 2,
          marker: { enabled: false },
          dashStyle: 'ShortDash',
          yAxis: yAxisIndex,
          showInLegend: false,
          linkedTo: ':previous',
        },
      ];
    };

    const series = selectedParameters.flatMap((key, index) =>
      buildSeriesForParameter(key, index)
    );

    const yAxis: Highcharts.YAxisOptions | Highcharts.YAxisOptions[] =
      selectedParameters.length === 2
        ? [
            {
              title: { text: getSeriesName(selectedParameters[0]) },
              opposite: false,
              minRange: 1,
              labels: {
                enabled: true,
                style: {
                  color: theme == 'light' ? '#363636' : '#ffffff',
                  fontSize: '11px',
                  fontWeight: '400',
                  fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
                },
              },
            },
            {
              title: { text: getSeriesName(selectedParameters[1]) },
              opposite: true,
              minRange: 1,
              labels: {
                enabled: true,
                style: {
                  color: theme == 'light' ? '#363636' : '#ffffff',
                  fontSize: '11px',
                  fontWeight: '400',
                  fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
                },
              },
            },
          ]
        : {
            title: { text: getSeriesName(selectedParameters[0]) },
            minRange: 1,
            labels: {
              enabled: true,
              style: {
                color: theme == 'light' ? '#363636' : '#ffffff',
                fontSize: '11px',
                fontWeight: '400',
                fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
              },
            },
          };

    return {
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        spacingLeft: 5,
      },
      title: { text: '' },
      credits: { enabled: false },
      xAxis: {
        type: 'datetime',
        lineColor: theme == 'light' ? '#E9EBF1' : '#4C5C68',
        labels: {
          style: {
            color: theme == 'light' ? '#363636' : '#ffffff',
            fontSize: '12px',
            fontWeight: '400',
            fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
          },
          formatter: function () {
            const date = new Date(this.value);

            return date.toLocaleTimeString('en-US', {
              timeZone: timeZone,
              hour: '2-digit',
              minute: '2-digit',
            });

          },
        },
      },
      yAxis,
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: 'transparent',
        borderWidth: 0,
        shadow: false,
        padding: 0,
        style: {
          pointerEvents: 'none',
        },
        positioner: function (labelWidth, labelHeight, point) {
          const chart = this.chart;
          const plotLeft = chart.plotLeft;
          const plotTop = chart.plotTop;
          const plotWidth = chart.plotWidth;
          const plotHeight = chart.plotHeight;
          let x = point.plotX + plotLeft + 10;
          let y = point.plotY + plotTop - labelHeight / 2;
          // Shift left if overflowing right edge
          if (x + labelWidth > plotLeft + plotWidth) {
            x = point.plotX + plotLeft - labelWidth - 10;
          }
          // Keep within left bound
          if (x < plotLeft) { x = plotLeft; }
          // Keep within top bound
          if (y < plotTop) { y = plotTop; }
          // Keep within bottom bound
          if (y + labelHeight > plotTop + plotHeight) {
            y = plotTop + plotHeight - labelHeight;
          }
          return { x, y };
        },
        formatter: function () {
          const x = (this as any).x as number | undefined;
          const points = (this as any).points as any[] | undefined;
          if (x == null || !points?.length) return '';

          const tooltipDate = new Date(x);
          const timePart = tooltipDate.toLocaleTimeString('en-US', { timeZone, hour: '2-digit', minute: '2-digit', hour12: true });
          const datePart = tooltipDate.toLocaleDateString('en-US', { timeZone, year: 'numeric', month: 'short', day: 'numeric' });
          const tzAbbr = getTimezoneAbbreviation(timeZone);

          const tooltipRows = points
            .map(point => {
              const parameterKey = point?.point?.options?.custom?.parameterKey as
                | PredictionParameterKey
                | undefined;
              const segment = point?.point?.options?.custom?.segment as
                | 'actual'
                | 'prediction'
                | undefined;
              const metricName = parameterKey ? getSeriesName(parameterKey) : point.series.name;
              const value = typeof point.y === 'number' ? point.y.toFixed(2) : '--';
              const segmentLabel = segment === 'prediction' ? ' (Pred)' : '';
              const color = point.color || '#005EE5';
              return `<div style="margin-bottom:4px; color:${color};">${metricName}${segmentLabel}: ${value}</div>`;
            })
            .join('');

          const firstPollutant =
            points.find(p => p?.point?.options?.custom?.primaryPollutant)?.point?.options?.custom
              ?.primaryPollutant || '';

          return `
            <div style="font-size:11px; min-width:min-content; max-width:340px; line-height:1.5; white-space:normal; word-wrap:break-word; overflow-wrap:break-word; font-family:'DM Sans','Inter',system-ui,sans-serif; background:#fff; padding:10px; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.15);">
              <div style="margin-bottom:4px; font-weight:600; white-space:nowrap;">${timePart} ${tzAbbr}, ${datePart}</div>
              ${tooltipRows}
              ${firstPollutant ? `<div style="margin-top:4px; font-size:11px; color: var(--color-text);">Main pollutant: ${formatChemicalLabel(firstPollutant)}</div>` : ''}
            </div>
          `;
        },
      },
      legend: {
        enabled: false,
      },
      series,
    };
  }, [
    actualDataByParameter,
    colorByParameter,
    predictions,
    predictionParameters,
    timeZone,
    userSelectedParameter,
    userSelectedParameter2,
  ]);

  useEffect(() => {
    if (!predictionParameters.length) return;

    const availableKeys = predictionParameters.map(parameter => parameter.key);

    if (!availableKeys.includes(userSelectedParameter)) {
      setUserSelectedParameter(availableKeys[0]);
      setUserSelectedParameter2('');
      return;
    }

    if (userSelectedParameter2 && !availableKeys.includes(userSelectedParameter2)) {
      setUserSelectedParameter2('');
    }
  }, [predictionParameters, userSelectedParameter, userSelectedParameter2]);

  return (
    <div className="device-overview-container scrollbar">
      <div className="device-overview-right-panel w-full">
        <div className="trend-analysis">
          <div className="trend-header mb-6">
            <div className="chart-title-section">
              <h3 className="trend-title">AQI Prediction</h3>
            </div>
            <div className="trend-controls">
              <div className="view-toggle">
                {(['1H', '6H'] as PredictionRange[]).map(range => (
                  <button
                    key={range}
                    onClick={() => setSelectedRange(range)}
                    className={`toggle-button ${selectedRange === range ? 'active' : ''}`}
                    style={{ width: '3rem', minWidth: '3rem', height: '2.25rem', minHeight: '2.25rem' }}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading || predictionLoading ? (
            <div className="loading-container device-overview-chart-container">
              <div className="h-8 w-8 border-b-2 border-[var(--gradient-button-left-color)] mb-4 animate-spin rounded-full"></div>
              <p className="text-base text-[var(--color-sub-title-text)]">
                Loading AQI prediction data...
              </p>
            </div>
          ) : predictionError ? (
            <div className="error-container device-overview-chart-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '1rem' }}>
                <path d="M3 3v18h18" />
                <path d="M7 16l4-4 4 4 5-5" />
              </svg>
              <p style={{ fontSize: '1rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>No Data Available</p>
               </div>
          ) : (
            <div className="device-overview-chart-container">
              <HighchartsReact
                highcharts={Highcharts}
                options={chartOptions}
                containerProps={{ style: { flex: '1 1 0%' } }}
              />
              <div className="metric-selectors">
                {predictionParameters.map(parameter => (
                  <button
                    key={parameter.key}
                    className={`metric-button ${
                      userSelectedParameter === parameter.key ||
                      userSelectedParameter2 === parameter.key
                        ? 'active'
                        : ''
                    }`}
                    onClick={() => handleParameterClick(parameter.key)}
                  >
                    {parameter.label === 'TEMPERATURE'
                      ? 'TEMP'
                      : parameter.label === 'HUMIDITY'
                        ? 'HUM'
                        : parameter.label}
                  </button>
                ))}
              </div>
              {predictionAdvisory && (
                <div className="mt-3 text-sm text-center leading-6">
                  <span className="md:inline block font-medium text-[var(--color-text)]">{predictionAdvisory.title}</span>{' '}
                  <span className="text-[var(--color-sub-title-text)]">{predictionAdvisory.body}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DevicePredection;
